import { Pagination } from 'antd';
import moment from 'moment';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import Container from '@/src/shared/components/Container';
import Head from '@/src/shared/components/Head';
import Heading from '@/src/shared/components/Heading';
import Hero from '@/src/shared/components/Hero';
import Layout from '@/src/shared/components/Layout';
import withMembershipAccessWrap, {
  mapStateToProps,
} from '@/src/shared/components/MembershipPageWrap';
import ResultCard from '@/src/shared/components/ResultCard';
import Section from '@/src/shared/components/Section';
import WebLogImage from '@/src/shared/components/WebLogImage';
import { ABOUT_ACCESS } from '@/src/shared/constants';
import { space } from '@/styles';
import request from '@/utils/axios';
import formatURL from '@/utils/formatURL';

/** @typedef {import('@/src/types/swagger').IBroadcastDetailsDTO} IBroadcastDetailsDTO */

const PAGE_SIZE = 20;

const CeoCommunications = () => {
  const router = useRouter();
  const [commData, setCommData] = useState(/** @type {IBroadcastDetailsDTO[]} */([]));

  useEffect(() => {
    const fetchPageData = async () => {
      /** @type {import('axios').AxiosResponse<IBroadcastDetailsDTO[]>} */
      const response = await request({
        url: `broadcast/list`,
        baseURL: process.env.V2_API_ROOT,
        params: {
          broadcastTypeId: 2,
        },
      });
      const { data } = response;
      data.sort((a, b) => (moment(a.completionDate) < moment(b.completionDate) ? 1 : -1));
      setCommData(data);
    };
    fetchPageData();
  }, []);

  const [current, setCurrent] = useState(
    router.query.page && !Number.isNaN(router.query.page) ? Number(router.query.page) : 1
  );

  function handlePaginationChange(page) {
    setCurrent(page);
    const newURL = formatURL(router.asPath, { page });
    router.replace(newURL, newURL, { shallow: true });
  }

  function renderResults() {
    const paginatedResults = commData.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
    if (!paginatedResults.length) return <div>No Data Available</div>;

    return paginatedResults.map((result, index) => {
      const date = moment(result.completionDate).format('MMMM YYYY');
      const showHeading =
        index === 0 ||
        date !== moment(paginatedResults[index - 1].completionDate).format('MMMM YYYY');
      return (
        <React.Fragment key={result.broadcastId}>
          {showHeading && (
            <Heading type={3} weight="bold" theme="primaryGray" css={[space.m.t20, space.m.b20]}>
              {date}
            </Heading>
          )}
          <ResultCard
            href="/about/membership/ceo-communications/[slug]"
            as={`/about/membership/ceo-communications/${result.broadcastId}`}
            title={result.subject || ''}
            subtitle={moment(result.completionDate).format('MM/DD/YYYY')}
            iconVisible={false}
          />
        </React.Fragment>
      );
    });
  }

  const pageTitle = 'CEO Communications';

  return (
    <Layout>
      <Head title={pageTitle} />
      <Hero title={pageTitle} />
      <Section bgColor="gray">
        <Container size="sm">
          <Pagination
            onChange={handlePaginationChange}
            total={commData.length}
            pageSize={PAGE_SIZE}
            current={current}
          />
          {renderResults()}
        </Container>
      </Section>
      <WebLogImage title={pageTitle} path="/about/membership/ceo-communications" />
    </Layout>
  );
};

export default connect(mapStateToProps)(
  withMembershipAccessWrap(CeoCommunications, ABOUT_ACCESS.CEO_COMMUNICATIONS, true)
);
